/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Fonts, Spacings } from '@styles/index';

import Checkbox from '@base/Checkbox/Checkbox';
import Link from '@base/Link/Link';
import H5 from '@base/H5/H5';
import Button from 'aquilla/core/molecules/Button';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { readCookie } from '@utils/cookies';
import { getMe } from '@api/selectors/auth';
import P1 from '@base/P1/P1';
import { useRouter } from 'next/router';
import { drawWOFSpin } from '@api/fetchers/rewards';
import useRewardEvent from '@api/fetchers/rewards/useRewardEvent';
import useRewardEvents from '@stores/rewards';
import { segment } from '@actions/analytics';
import { compareDates, dateWithTime } from '@utils/dates';
import { CHECKOUT_STARTED } from '@constants/segment';
import { fetchPinnedGoals } from '@api/fetchers/goal/updaters';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import SpinnerCenterIcon from './SpinnerCenterIcon';
import SpinnerArrow from './SpinnerArrow';
import SpinWheel, {
  Event,
  PrizeDetails,
  PrizeItem,
  SpinWheelItem,
  UserPrizeDetail
} from './SpinWheel';
import REWARD_ITEM_TYPE from './constants';
import {
  LUCKY_DRAW_CLICKED_LUCKY_DRAW,
  LUCKY_DRAW_CLICKED_CTA,
  LUCKY_DRAW_CLICKED_REGISTER
} from './constants/event';

const WOFContainer = styled.div`
  width: 100vw;
  height: 100vh;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  padding-bottom: ${Spacings.SPACING_6B};
`;

const TopContainer = styled.div`
  width: 100%;
`;

const EventInfo = styled.div`
  padding: ${Spacings.SPACING_4B};
  padding-top: ${Spacings.SPACING_8B};
  padding-bottom: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
`;

const LogoImageContainer = styled.div`
  height: 80px;
  width: 300px;
  position: relative;
`;

const UALogo = styled(Image)``;

const EventDescription = styled(H5)`
  text-align: center;
  padding: 0 ${Spacings.SPACING_4B};
`;

const CongratulationText = styled.p`
  text-align: center;
  padding: 0 ${Spacings.SPACING_4B};
  font-size: 28px;
  font-weight: 600;
  margin-bottom: ${Spacings.SPACING_6B};
`;

const BottomContainer = styled.div`
  gap: ${Spacings.SPACING_8B};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ActionButton = styled<any>(Button)`
  width: ${Spacings.SPACING_60B};
  margin-bottom: -${Spacings.SPACING_4B};
  background-color: ${({ color }) => color} !important;

  :disabled {
    outline: none !important;
    border: none !important;
    opacity: 50%;
  }
`;

const ConsentDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${Spacings.SPACING_4B};
`;

const StyledSpan = styled.span`
  font-size: ${Fonts.H6};
  color: ${({ color }) => color};

  a {
    color: ${({ color }) => color};
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
    text-decoration: underline;
  }
`;

const WOFSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    position: absolute;
    top: 20px;
  }

  .spinner-center-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const WOFWonPrizeContainer = styled.div`
  border: 2px solid #e9eef2;
  border-radius: 18px;
  overflow: hidden;
  width: 240px;
`;

const WonPrizeContainerTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 8px;
`;
const WonPrizeContainerBottom = styled.div`
  background-color: var(--color-i-white);
  padding: 12px 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const PrizeTitle = styled(H5)`
  color: ${({ color }) => color};
  text-align: center;
`;
const PrizeDescription = styled(P1)`
  color: ${({ color }) => color};
  text-align: center !important;
  width: 100%;
`;

const ReferenceID = styled(P1)`
  color: var(--color-i-white);
  font-weight: 600 !important;
  margin: -${Spacings.SPACING_2B} 0;
`;

const AnimationContainer = styled(LottieAnimation)`
  position: absolute;
  width: calc(100vw);
  height: calc(100%);
  top: 0;
  bottom: 0;
  margin: auto;
  left: 0;
  right: 0;
`;

const getWonPrizeItemIndex = (prizeItems: PrizeItem[], prizeUid: string) => {
  return prizeItems.findIndex((item) => item.uid === prizeUid);
};
const getWonPrizeItem = (prizeItems: PrizeItem[], prizeUid: string) => {
  return prizeItems.find((item) => item.uid === prizeUid);
};
const RewardsWOF: FC<{
  eventUID: string;
  rewardEventDetails: Event;
  fallbackRewardPrizeDetails: PrizeDetails;
}> = ({ eventUID, rewardEventDetails, fallbackRewardPrizeDetails }) => {
  const [agreedToTnC, setAgreedToTnC] = useState(true);
  const [prizeWonItem, setPrizeWonItem] = useState<
    UserPrizeDetail & PrizeItem
  >();
  const [prizeWonIndex, setPrizeWonIndex] = useState<number | undefined>();
  const [hasSpinCompleted, setHasSpinCompleted] = useState(false);
  const [hideLottie, setHideLottie] = useState(false);
  const [isWheelSpinning, setIsWheelSpinning] = useState(false);
  const [loadingPrizeDraw, setLoadingPrizeDraw] = useState(false);
  const [activePinnedGoal, setActivePinnedGoal] = useState(
    {} as Record<string, string>
  );

  const wheelCtxRef = useRef(null);
  const router = useRouter();

  const userInfo = useMyInfo(getMe);
  const isLoggedIn = !!readCookie('accessToken') && !!userInfo;
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const {
    isValidating: isFetchingPrize,
    userWonPrizeDetails,
    prizeEventDetails
  } = useRewardEvent({ fallbackData: fallbackRewardPrizeDetails }, eventUID);

  const sortedPrizeItemsByRank = useMemo(
    () =>
      (fallbackRewardPrizeDetails?.prizeItems || [])?.sort(
        (a, b) => a.rank - b.rank
      ),
    [fallbackRewardPrizeDetails]
  );

  const isEventStarted =
    compareDates(rewardEventDetails?.startDate, Date.now()) !== -1;
  const isEventEnded =
    compareDates(Date.now(), rewardEventDetails?.endDate) === -1;

  const { selectedGoal, setCityStateMapping, selectedCity } =
    useRewardEvents() as {
      selectedGoal: any;
      setCityStateMapping: (
        mapping: Array<{ state: string; cities: string[] }>
      ) => void;
      selectedCity: string;
    };

  useEffect(() => {
    if (rewardEventDetails?.cityStateMapping) {
      setCityStateMapping(rewardEventDetails.cityStateMapping);
    }
  }, [rewardEventDetails, setCityStateMapping]);

  const spinWheelItems: SpinWheelItem[] = sortedPrizeItemsByRank.map(
    (item, index) => {
      const isOddIndex = index % 2 !== 0;
      return {
        key: item.uid,
        label: item.name,
        bgColor: isOddIndex
          ? rewardEventDetails.primaryColor
          : rewardEventDetails.secondaryColor,
        imageUrl: item.eventIcon
      };
    }
  );

  useEffect(() => {
    const handlePopState = () => {
      window.location.reload();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchPinnedGoals().then((followedGoals) => {
        if (followedGoals.length) {
          setActivePinnedGoal(followedGoals[0]);
        }
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (userWonPrizeDetails && userWonPrizeDetails?.prizeUid) {
      const wonPrizeItemIndex = getWonPrizeItemIndex(
        sortedPrizeItemsByRank,
        userWonPrizeDetails?.prizeUid
      );
      const wonPrizeItem = getWonPrizeItem(
        prizeEventDetails.prizeItems,
        userWonPrizeDetails?.prizeUid
      );
      setHasSpinCompleted(true);
      setPrizeWonIndex(wonPrizeItemIndex);
      setPrizeWonItem((prev) => {
        return {
          ...prev,
          ...wonPrizeItem,
          ...userWonPrizeDetails
        };
      });
    }
  }, [prizeEventDetails, sortedPrizeItemsByRank, userWonPrizeDetails]);

  const renderCTAButtonOnWin = (
    showButton: boolean,
    buttonText: string,
    buttonLink?: string
  ) => {
    if (showButton) {
      return (
        <ActionButton
          color={rewardEventDetails.primaryColor}
          size="large"
          onClick={() => {
            segment.track(LUCKY_DRAW_CLICKED_CTA, {
              event_name: rewardEventDetails.eventName,
              gift_won: prizeWonItem?.name,
              cta_button: buttonText,
              ...(selectedCity && { city: selectedCity })
            });
            if (buttonLink) window.open(buttonLink);
          }}
        >
          {buttonText}
        </ActionButton>
      );
    }
  };

  return (
    <WOFContainer
      style={{
        backgroundImage: `url(${rewardEventDetails?.bgImageUrl})`
      }}
    >
      <TopContainer>
        <EventInfo>
          <LogoImageContainer>
            <UALogo
              src={rewardEventDetails.headerLogoUrl}
              fill
              alt={rewardEventDetails.eventName}
            />
          </LogoImageContainer>
          {hasSpinCompleted && prizeWonIndex ? (
            <CongratulationText
              style={{ color: rewardEventDetails.descriptionColor }}
            >
              Congrats! {userInfo.firstName}
            </CongratulationText>
          ) : (
            <EventDescription
              style={{ color: rewardEventDetails.descriptionColor }}
            >
              {rewardEventDetails.headerDescription}
            </EventDescription>
          )}
        </EventInfo>
      </TopContainer>
      <BottomContainer>
        {hasSpinCompleted && prizeWonIndex >= 0 ? (
          <WOFWonPrizeContainer>
            <WonPrizeContainerTop
              style={{
                backgroundColor: rewardEventDetails.prizeContainerBgColor
              }}
            >
              <Image
                alt={prizeWonItem?.name}
                src={prizeWonItem?.prizeIcon}
                width="160"
                height="160"
              />
            </WonPrizeContainerTop>
            <WonPrizeContainerBottom>
              <PrizeTitle color={rewardEventDetails.primaryColor}>
                You won a {prizeWonItem.name}
              </PrizeTitle>
              <PrizeDescription color={rewardEventDetails.primaryColor}>
                {prizeWonItem.prizeText}
              </PrizeDescription>
            </WonPrizeContainerBottom>
          </WOFWonPrizeContainer>
        ) : (
          <WOFSpinnerContainer>
            <SpinnerArrow
              borderColor={rewardEventDetails.secondaryColor}
              bgColor={rewardEventDetails.primaryColor}
            />
            <SpinnerCenterIcon
              borderColor={rewardEventDetails.secondaryColor}
              bgColor={rewardEventDetails.primaryColor}
              iconColor="white"
            />
            <SpinWheel
              wheelCtxRef={wheelCtxRef}
              spinWheelItems={spinWheelItems}
              rewardEventDetails={rewardEventDetails}
              onSpinStart={() => {
                setIsWheelSpinning(true);
              }}
              onSpinStop={() => {
                setHasSpinCompleted(true);
                setIsWheelSpinning(false);
              }}
            />
          </WOFSpinnerContainer>
        )}
        {isEventStarted && !isEventEnded ? (
          <>
            {isLoggedIn ? (
              <>
                {hasSpinCompleted ? (
                  <>
                    <ReferenceID>
                      Ref. id: {prizeWonItem.referenceId}
                    </ReferenceID>
                    {renderCTAButtonOnWin(
                      prizeWonItem.prizeType !== REWARD_ITEM_TYPE.COUPON,
                      rewardEventDetails.ctaButtonText3,
                      rewardEventDetails.ctaButtonText3Link
                    )}
                  </>
                ) : (
                  <ActionButton
                    color={rewardEventDetails.primaryColor}
                    size="large"
                    isLoading={
                      isFetchingPrize || loadingPrizeDraw || isWheelSpinning
                    }
                    disabled={
                      !agreedToTnC ||
                      isWheelSpinning ||
                      loadingPrizeDraw ||
                      isFetchingPrize
                    }
                    onClick={async () => {
                      segment.track(LUCKY_DRAW_CLICKED_LUCKY_DRAW, {
                        event_name: rewardEventDetails.eventName,
                        goal_name: selectedGoal.name,
                        goal_uid: selectedGoal.uid,
                        ...(selectedCity && { city: selectedCity })
                      });
                      setLoadingPrizeDraw(true);
                      const res = await drawWOFSpin(eventUID);
                      const wonPrizeItemIndex = getWonPrizeItemIndex(
                        sortedPrizeItemsByRank,
                        res?.prizeUid
                      );
                      const wonPrizeItem = getWonPrizeItem(
                        prizeEventDetails.prizeItems,
                        res?.prizeUid
                      );
                      setLoadingPrizeDraw(false);
                      setPrizeWonIndex(wonPrizeItemIndex);
                      setPrizeWonItem(() => {
                        return {
                          ...wonPrizeItem,
                          ...res
                        };
                      });
                      wheelCtxRef.current.spinToItem(
                        wonPrizeItemIndex,
                        10000,
                        true,
                        4,
                        1
                      );
                    }}
                  >
                    {rewardEventDetails.ctaButtonText2}
                  </ActionButton>
                )}
              </>
            ) : (
              <ActionButton
                color={rewardEventDetails.primaryColor}
                size="large"
                onClick={() => {
                  segment.track(LUCKY_DRAW_CLICKED_REGISTER, {
                    event_name: rewardEventDetails.eventName,
                    ...(selectedCity && { city: selectedCity })
                  });
                  openLoginDrawer();
                }}
              >
                {rewardEventDetails.ctaButtonText1}
              </ActionButton>
            )}
          </>
        ) : null}
        {!isEventStarted && !isEventEnded ? (
          <p style={{ color: rewardEventDetails.bgColor, fontWeight: 600 }}>
            Starts on {dateWithTime(rewardEventDetails.startDate)}
          </p>
        ) : null}
        {isEventEnded ? (
          <p style={{ color: rewardEventDetails.bgColor, fontWeight: 600 }}>
            Event has ended
          </p>
        ) : null}
        {isLoggedIn && !hasSpinCompleted && isEventStarted && !isEventEnded && (
          <ConsentDiv onClick={() => setAgreedToTnC((prev) => !prev)}>
            <Checkbox color="white" size="medium" checked={agreedToTnC} />
            <StyledSpan color="white">
              By continuing I agree to{' '}
              <Link href="/terms" newTab onClick={(e) => e.stopPropagation()}>
                Terms & Conditions
              </Link>{' '}
              & I affirm that I am not a resident of Tamil Nadu.
            </StyledSpan>
          </ConsentDiv>
        )}
        {isLoggedIn && hasSpinCompleted && (
          <ConsentDiv onClick={() => setAgreedToTnC((prev) => !prev)}>
            <StyledSpan color="white">
              <Link href="/terms" newTab onClick={(e) => e.stopPropagation()}>
                Terms & Conditions
              </Link>{' '}
            </StyledSpan>
          </ConsentDiv>
        )}
      </BottomContainer>
      {hasSpinCompleted && !hideLottie && (
        <AnimationContainer
          loop={false}
          events={{ complete: () => setHideLottie(true) }}
          src={getAnimationPath('unacademyIcons/confetti.json')}
        />
      )}
    </WOFContainer>
  );
};

export default RewardsWOF;
